/**
 * Subscription state for the signed-in athlete.
 *
 * `/billing/my-subscription` is on the paywall whitelist, so this hook keeps
 * answering even when every other endpoint is returning 402 — which is exactly
 * when the answer matters most.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

export interface Subscription {
  tier: string;
  status: string;
  currency: string;
  billing_interval: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  grace_period_until: string | null;
}

/** Whole days from now until `iso`, floored at 0. Null when there is no date. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000));
}

export function useSubscription() {
  const token = useAuthStore((s) => s.token);

  const query = useQuery({
    queryKey: ["my-subscription"],
    enabled: !!token,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<{
        subscription: Subscription | null;
        paywall_exempt?: boolean;
      }>("/billing/my-subscription");
      return data;
    },
  });

  const sub = query.data?.subscription ?? null;
  // Mirrors PaywallMiddleware: owner/admin accounts are never gated, so they
  // must not be nagged either. Read from the API rather than recomputed here —
  // the banner disagreeing with the gate is what produced a permanent
  // "your plan ends in 0 days" bar on an account the backend was letting through.
  const paywallExempt = query.data?.paywall_exempt === true;
  const trialing = !paywallExempt && sub?.status === "trialing";

  return {
    subscription: sub,
    isLoading: query.isLoading,
    trialing,
    /** Days left in the trial, or null when not trialing. */
    trialDaysLeft: trialing ? daysUntil(sub?.current_period_end) : null,
    /** True while a past_due subscription still has grace left. */
    paywallExempt,
    inGrace:
      !paywallExempt &&
      sub?.status === "past_due" &&
      (daysUntil(sub?.grace_period_until) ?? 0) > 0,
    graceDaysLeft:
      sub?.status === "past_due" ? daysUntil(sub?.grace_period_until) : null,
    endingSoon: !paywallExempt && !!sub?.cancel_at_period_end,
  };
}
